import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  IconButton,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { Permission, Role } from "./types/permissions";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import CustomTextField from "../../../components/common/Textfield";
import permissionService from "../../../service/permissionService"; 

interface PermissionsManagementProps {
  role: Role;
  onBack: () => void;
  onUpdatePermissions: (roleId: number, permissions: number[]) => void;
}

const PermissionsManagement: React.FC<PermissionsManagementProps> = ({
  role,
  onBack,
  onUpdatePermissions,
}) => {
  
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [granted, setGranted] = useState<Set<number>>(
    new Set(role.permissions),
  );
  const [expandedApps, setExpandedApps] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);

  const reduxTheme = useSelector((state: RootState) => state.theme);

  useEffect(() => {
    const loadPermissions = async () => {
      setLoadingPermissions(true);
      try {
        const data = await permissionService.getAllPermissions();
        setAllPermissions(data);
      } catch (error) {
        console.error("Failed to load permissions:", error);
      } finally {
        setLoadingPermissions(false);
      }
    };

    loadPermissions();
  }, []);

  useEffect(() => {
    setGranted(new Set(role.permissions));
  }, [role.permissions]);

  const hasUnsavedChanges = useMemo(() => {
    const originalSet = new Set(role.permissions);
    if (originalSet.size !== granted.size) return true;
    for (const id of originalSet) {
      if (!granted.has(id)) return true;
    }
    return false;
  }, [granted, role.permissions]);

  const togglePermission = useCallback((id: number) => {
    setGranted((prev) => {
      const newGranted = new Set(prev);
      if (newGranted.has(id)) {
        newGranted.delete(id);
      } else {
        newGranted.add(id);
      }
      return newGranted;
    });
  }, []);

  const toggleApp = useCallback((app: string) => {
    setExpandedApps((prev) => ({ ...prev, [app]: !prev[app] }));
  }, []);

  const toggleAllPermissionsForApp = useCallback(
    (app: string, perms: Permission[]) => {
      setGranted((prev) => {
        const newGranted = new Set(prev);
        const allPermIds = perms.map((p) => p.id);
        const allSelected = allPermIds.every((id) => newGranted.has(id));

        if (allSelected) {
          allPermIds.forEach((id) => newGranted.delete(id));
        } else {
          allPermIds.forEach((id) => newGranted.add(id));
        }

        return newGranted;
      });
    },
    [],
  );

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdatePermissions(role.id, Array.from(granted));
    } finally {
      setIsSaving(false);
    }
  };

  const groupedPermissions = useMemo(() => {
    const grouped: Record<string, Permission[]> = {};

    if (Array.isArray(allPermissions)) {
      allPermissions.forEach((p) => {
        const app = p.content_type.app_label;
        if (!grouped[app]) grouped[app] = [];
        grouped[app].push(p);
      });
    }

    return grouped;
  }, [allPermissions]);

  const filteredGrouped = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return groupedPermissions;

    const result: Record<string, Permission[]> = {};
    Object.entries(groupedPermissions).forEach(([app, perms]) => {
      const matches = perms.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.content_type.app_label.toLowerCase().includes(term) ||
          p.content_type.model.toLowerCase().includes(term),
      );
      if (matches.length > 0) {
        result[app] = matches;
      }
    });
    return result;
  }, [searchTerm, groupedPermissions]);

  const isAppFullyGranted = useCallback(
    (perms: Permission[]) => perms.every((p) => granted.has(p.id)),
    [granted],
  );

  const isAppPartiallyGranted = useCallback(
    (perms: Permission[]) => {
      const some = perms.some((p) => granted.has(p.id));
      const all = perms.every((p) => granted.has(p.id));
      return some && !all;
    },
    [granted],
  );

  if (loadingPermissions) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!Array.isArray(allPermissions) || allPermissions.length === 0) {
    return (
      <Box p={3}>
        <Typography color="error">No permissions available</Typography>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
    >
      <Box>
        <Paper sx={{ boxShadow: 0, p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <IconButton onClick={onBack} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box flexGrow={1}>
              <Typography variant="h5" fontWeight="bold">
                {role.name} Permissions
              </Typography>
            </Box>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={isSaving || !hasUnsavedChanges}
              startIcon={isSaving ? <CircularProgress size={20} /> : null}
              sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
            >
              {isSaving ? "Saving..." : "Save Permissions"}
            </Button>
          </Box>
        </Paper>

        <CustomTextField
          placeholder="Search permissions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        />

        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 0,
            maxHeight: "calc(100vh - 320px)",
            overflow: "auto",
          }}
        >
          <Table>
            <TableHead
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: `${reduxTheme.primary_colors[400]} !important`,
                "& th": {
                  color: "white !important",
                  fontWeight: "bold",
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ width: "5%" }} />
                <TableCell sx={{ width: "60%" }}>Permission</TableCell>
                <TableCell align="center" sx={{ width: "35%" }}>
                  Granted
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(filteredGrouped).map(([app, perms]) => {
                const isAllGranted = isAppFullyGranted(perms);
                const isPartial = isAppPartiallyGranted(perms);

                return (
                  <React.Fragment key={app}>
                    <TableRow>
                      <TableCell>
                        <IconButton size="small" onClick={() => toggleApp(app)}>
                          {expandedApps[app] ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <strong>
                          {app.charAt(0).toUpperCase() + app.slice(1)}
                        </strong>{" "}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                        >
                          ({perms.length})
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={isAllGranted}
                          indeterminate={isPartial}
                          onChange={() =>
                            toggleAllPermissionsForApp(app, perms)
                          }
                          color="primary"
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1, verticalAlign: "middle" }}
                        >
                          {perms.filter((p) => granted.has(p.id)).length}/
                          {perms.length}
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={3} sx={{ py: 0, borderBottom: 0 }}>
                        <Collapse in={expandedApps[app]} timeout="auto">
                          {perms.map((perm) => (
                            <TableRow
                              key={perm.id}
                              sx={{
                                "&:hover": { backgroundColor: "action.hover" },
                                "&:last-child td": { borderBottom: 0 },
                              }}
                            >
                              <TableCell />
                              <TableCell>
                                <Typography variant="body2">
                                  {perm.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {perm.content_type.model}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={granted.has(perm.id)}
                                  onChange={() => togglePermission(perm.id)}
                                  color="primary"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" gap={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setGranted(new Set())}
              disabled={granted.size === 0}
            >
              Clear All
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setGranted(new Set(allPermissions.map((p) => p.id)))
              }
              disabled={granted.size === allPermissions.length}
            >
              Select All
            </Button>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};

export default PermissionsManagement;
